import { useState, useEffect } from 'react';
import { getAllRubrics, getAllFunds } from '../../../services/firestoreService';
import Badge from '../../shared/UI/Badge';
import Button from '../../shared/UI/Button';

function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  return new Date(val);
}

function formatDate(val) {
  const d = toDate(val);
  return d ? d.toLocaleDateString() : '—';
}

// Returns the active rubric for a fund: published with the most recent published_at
function getActiveRubric(rubrics) {
  const published = rubrics.filter(r => r.is_published);
  if (!published.length) return null;
  return published.reduce((best, r) => {
    const bd = toDate(best.published_at) || toDate(best.created_at) || new Date(0);
    const rd = toDate(r.published_at) || toDate(r.created_at) || new Date(0);
    return rd > bd ? r : best;
  });
}

function RubricRow({ rubric, isActive, label, onEdit, onPreview }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 16px',
      borderTop: '1px solid var(--color-border)',
      background: isActive ? 'rgba(34,197,94,0.04)' : 'transparent',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: isActive ? 600 : 400, fontSize: 14 }}>
            {rubric.name}
          </span>
          {isActive
            ? <Badge text="Active" variant="success" />
            : <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{label}</span>
          }
          {rubric.is_published && (
            <span title="Published — locked" style={{ fontSize: 12 }}>🔒</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
          v{rubric.version || 1}
          {rubric.is_published
            ? ` · Published ${formatDate(rubric.published_at || rubric.created_at)}`
            : ` · Draft · Last modified ${formatDate(rubric.created_at)}`
          }
          {rubric.stream_id && ` · Stream: ${rubric.stream_id}`}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {rubric.is_published
          ? <Button size="sm" variant="secondary" onClick={() => onEdit(rubric, true)}>New Version</Button>
          : <Button size="sm" variant="secondary" onClick={() => onEdit(rubric, false)}>Edit</Button>
        }
        <Button size="sm" variant="ghost" onClick={() => onPreview(rubric)}>Preview</Button>
      </div>
    </div>
  );
}

function FundRubricGroup({ fundName, rubrics, onEdit, onPreview }) {
  const [showOld, setShowOld] = useState(false);

  const active = getActiveRubric(rubrics);
  const rest = rubrics
    .filter(r => r.id !== active?.id)
    .sort((a, b) => {
      const ad = toDate(a.published_at || a.created_at) || new Date(0);
      const bd = toDate(b.published_at || b.created_at) || new Date(0);
      return bd - ad;
    });

  const visibleOld = showOld ? rest : rest.slice(0, 2);
  const hiddenCount = rest.length - 2;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{fundName}</span>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
          {rubrics.length} rubric{rubrics.length !== 1 ? 's' : ''}
        </span>
      </div>

      {active && (
        <RubricRow
          rubric={active}
          isActive
          label=""
          onEdit={onEdit}
          onPreview={onPreview}
        />
      )}

      {visibleOld.map((r, i) => (
        <RubricRow
          key={r.id}
          rubric={r}
          isActive={false}
          label="Previous version"
          onEdit={onEdit}
          onPreview={onPreview}
        />
      ))}

      {!showOld && hiddenCount > 0 && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={() => setShowOld(true)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-accent)',
              cursor: 'pointer',
              fontSize: 13,
              padding: 0,
            }}
          >
            Show {hiddenCount} older version{hiddenCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {!active && rest.length === 0 && (
        <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          No rubrics
        </div>
      )}
    </div>
  );
}

export default function RubricList({ onAdd, onEdit, onPreview }) {
  const [rubrics, setRubrics] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function load() {
    setLoading(true);
    Promise.all([getAllRubrics(), getAllFunds()])
      .then(([r, f]) => { setRubrics(r); setFunds(f); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  if (error) return <div className="alert alert-error">{error}</div>;
  if (loading) return <div style={{ padding: 24, color: 'var(--color-text-secondary)' }}>Loading…</div>;

  // Group rubrics by fund_id
  const grouped = {};
  rubrics.forEach(r => {
    const fid = r.fund_id || '__none__';
    if (!grouped[fid]) grouped[fid] = [];
    grouped[fid].push(r);
  });

  // Sort fund groups: funds with a known name first
  const sortedFundIds = Object.keys(grouped).sort((a, b) => {
    const nameA = funds.find(f => f.id === a)?.name || a;
    const nameB = funds.find(f => f.id === b)?.name || b;
    return nameA.localeCompare(nameB);
  });

  return (
    <div>
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
          The rubric marked <strong>Active</strong> is currently being used to score new PIF submissions
          for that fund. Previous versions are kept for reference only.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button variant="primary" onClick={onAdd}>+ Add New Rubric</Button>
      </div>

      {sortedFundIds.map(fid => (
        <FundRubricGroup
          key={fid}
          fundName={funds.find(f => f.id === fid)?.name || fid}
          rubrics={grouped[fid]}
          onEdit={onEdit}
          onPreview={onPreview}
        />
      ))}

      {rubrics.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: 12 }}>No rubrics configured</p>
          <Button variant="primary" onClick={onAdd}>+ Add Rubric</Button>
        </div>
      )}
    </div>
  );
}
